import { Box, Stack, Typography, styled, Tooltip } from '@mui/material';
import React from 'react';

import { colors } from '../theme/theme';

import CommonStatusChip from './StatusChipComponent';

interface StatusChipProps {
    label: string;
    chipBgColor?: string;
    chipBorderColor?: string;
    chipTextColor?: string;
}

interface DashboardCardProps {
    title: string;
    value: string | number;
    icon: string;
    subText?: string;
    iconColor?: string;
    iconBgColor?: string;
    handleRedirect?: () => void;
    statusChipProps?: StatusChipProps;
    infoText?: string;
    iconPadding?: number;
    titleColor?: string;
}

export const CardContainer = styled(Box)<{ hasOnClick?: boolean }>(({ theme, hasOnClick }) => ({
    backgroundColor: colors.primaryLight[5],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    height: '100%',
    width: '100%',
    cursor: hasOnClick ? 'pointer' : 'default',
}));

const SummaryCard: React.FC<DashboardCardProps> = ({
    title,
    value,
    icon,
    subText,
    iconBgColor = colors.primaryLight[70],
    iconColor,
    handleRedirect,
    statusChipProps,
    infoText,
    iconPadding = 1.25,
    titleColor,
}) => {
    return (
        <CardContainer onClick={handleRedirect} hasOnClick={!!handleRedirect}>
            <Stack
                className="summaryCard"
                direction={'row'}
                gap={1.25}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Stack gap={1.5} overflow={'hidden'} minWidth={0}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} minWidth={0}>
                        <Tooltip title={title}>
                            <Typography
                                variant="body1"
                                color={titleColor ?? 'textSecondary'}
                                lineHeight={'21px'}
                                minWidth={0}
                                width={{ xs: '100%', sm: 'auto' }}
                                sx={{
                                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                    overflow: { xs: 'visible', sm: 'hidden' },
                                    textOverflow: { xs: 'clip', sm: 'ellipsis' },
                                }}
                            >
                                {title}
                            </Typography>
                        </Tooltip>
                        {(infoText || statusChipProps) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                {infoText && (
                                    <Tooltip title={infoText}>
                                        <span
                                            className="icon-info-circle"
                                            style={{ fontSize: 20, color: colors.fullBlack }}
                                        />
                                    </Tooltip>
                                )}
                                {statusChipProps && (
                                    <CommonStatusChip
                                        label={statusChipProps.label}
                                        chipBgColor={statusChipProps.chipBgColor}
                                        chipBorderColor={statusChipProps.chipBorderColor}
                                        chipTextColor={statusChipProps.chipTextColor}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                    <Typography variant="h5" color="textPrimary">
                        {value}
                    </Typography>
                </Stack>
                <Stack
                    sx={{
                        borderRadius: 3,
                        bgcolor: iconBgColor,
                        p: iconPadding,
                        width: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 'fit-content',
                    }}
                >
                    <span
                        className={icon}
                        style={{
                            fontSize: 24,
                            color: iconColor || colors.fullWhite,
                        }}
                    />
                </Stack>
            </Stack>
            {subText && (
                <Typography
                    variant="body2"
                    sx={{
                        bgcolor: colors.fullWhite,
                        color: colors.warningPreset[20],
                        mt: 1.5,
                        py: 0.5,
                        px: 1,
                        borderRadius: 2,
                        width: 'fit-content',
                        lineHeight: '21px',
                    }}
                >
                    {subText}
                </Typography>
            )}
        </CardContainer>
    );
};

export default SummaryCard;
